<template>
  <v-container fluid class="pa-6">
    <div class="d-flex justify-space-between align-center mb-6">
      <h1 class="text-h5 font-weight-bold text-grey-darken-4">Lista de Anúncios</h1>
      <!--<v-btn color="primary" @click="handleCreateAdvertisement">
        <v-icon start>mdi-plus</v-icon>
        Adicionar Novo Anúncio
      </v-btn>-->
    </div>

    <v-card class="elevation-4">
      <v-card-text>
        <v-row class="mb-6" dense>
          <v-col cols="12" md="5">
            <v-text-field v-model="searchTerm" placeholder="Pesquisar por título, código ou cidade" clearable
              hide-details prepend-inner-icon="mdi-magnify" @input="onFiltersChange" />
          </v-col>
          <v-col cols="12" md="4">
            <v-select v-model="selectedCategory" :items="categoryOptions" item-title="description" item-value="id" 
              placeholder="Filtrar por Categoria" clearable hide-details @update:model-value="onFiltersChange" />
          </v-col>
          <v-col cols="12" md="3">
            <v-select v-model="selectedStatus" :items="statusOptions" placeholder="Filtrar por Status" clearable
              hide-details @update:model-value="onFiltersChange" />
          </v-col>
        </v-row>

        <v-data-table-server :headers="columns" :items="paginatedAdvertisements" :items-length="totalItems"
          :loading="isLoading" :items-per-page="itemsPerPage" :page="page" :no-data-text="noDataMessage"
          class="elevation-0" @update:page="page = $event" @update:items-per-page="itemsPerPage = $event">

          <template v-slot:item.code="{ item }">
            <span>{{ item.code }}</span>
          </template>

          <template v-slot:item.category="{ item }">
            <span>{{ item.category?.description || 'N/A' }}</span>
          </template>

          <template v-slot:item.amount="{ item }">
            <span>R$ {{ item.amount ? parseFloat(item.amount).toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : 'A combinar' }}</span>
          </template>

          <template v-slot:item.status="{ item }">
            <v-chip :color="statusTagMap[item.status]" label>
              {{ formatStatus(item.status) }}
            </v-chip>
          </template>

          <template v-slot:item.is_featured="{ item }">
            <v-chip :color="isActiveTagMap[item.is_featured]" label>
              {{ formatIsFeatured(item.is_featured) }}
            </v-chip>
          </template>

          <template v-slot:item.is_active="{ item }">
            <v-chip :color="isActiveTagMap[item.is_active]" label>
              {{ formatIsActive(item.is_active) }}
            </v-chip>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-icon icon="mdi-check-circle-outline" size="large" color="info" class="mr-2"
              @click="handleStatusChange(item)" title="Alterar Status" />
            <v-icon icon="mdi-pencil" color="warning" size="large" class="mr-2" @click="handleEdit(item)"
              title="Editar" />
            <!--<v-icon icon="mdi-delete" color="error" size="large" @click="handleDelete(item)" title="Excluir" />-->
          </template>
        </v-data-table-server>

        <v-alert v-if="hasError && !isLoading" type="error" variant="tonal" class="mt-4 text-center">
          Ocorreu um erro ao carregar os dados.
        </v-alert>
      </v-card-text>
    </v-card>

  </v-container>

  <v-dialog v-model="deleteDialog" max-width="500">
    <v-card>
      <v-card-title class="text-h5">Confirmar Exclusão</v-card-title>
      <v-card-text>
        Tem certeza de que deseja excluir o anúncio
        <strong>{{ itemToDelete.title }}</strong>?
        <br /><br />
        Código: <strong>{{ itemToDelete.code }}</strong>
      </v-card-text>
      <v-card-actions class="d-flex justify-space-between">
        <v-btn color="secondary" variant="flat" @click="deleteDialog = false">
          Cancelar
        </v-btn>
        <v-btn color="error" variant="flat" @click="confirmDelete">
          Excluir
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog v-model="statusDialog" max-width="500">
    <v-card>
      <v-card-title class="text-h5">Alterar Status do Anúncio</v-card-title>
      <v-card-text>
        <p class="mb-4">
          Anúncio: <strong>{{ advertisementToUpdate.title }}</strong> (Cód:
          <strong>{{ advertisementToUpdate.code }}</strong>)
        </p>

        <v-select v-model="newStatusData.status" :items="statusOptions" item-title="title" item-value="value"
          label="Novo Status" :rules="[v => !!v || 'O status é obrigatório']" variant="outlined" density="compact"
          class="mt-4"></v-select>

        <v-switch v-model="newStatusData.notifyClient" color="primary" hide-details class="mt-4">
          <template #label>
            Notificar Cliente sobre alteração?
            <v-tooltip location="top">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="small" class="ml-1" color="grey-darken-1">mdi-information-outline</v-icon>
              </template>
              <span>
                Se 'Sim', o cliente será notificado (via email/sistema) sobre a
                aprovação ou rejeição.
              </span>
            </v-tooltip>
          </template>
        </v-switch>
      </v-card-text>
      <v-card-actions class="d-flex justify-space-between">
        <v-btn color="secondary" variant="flat" @click="statusDialog = false">
          Cancelar
        </v-btn>
        <v-btn color="primary" variant="flat" :loading="isStatusSubmitting" :disabled="isStatusSubmitting"
          @click="confirmStatusChange">
          Salvar Status
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import api from '@/services/api'
import { useToastStore } from '@/store/toast'
import { useLoadingStore } from '@/store/loadingStore' 

const router = useRouter()
const toastStore = useToastStore()
const loadingStore = useLoadingStore() 

const userData = ref(null)
const advertisements = ref([])
const categories = ref([]) 
const isLoading = ref(true)
const hasError = ref(false)
const searchTerm = ref('')
const selectedStatus = ref(null)
const selectedCategory = ref(null) 

const page = ref(1)
const itemsPerPage = ref(10)
const totalItems = ref(0)

const deleteDialog = ref(false)
const itemToDelete = ref({})

const statusDialog = ref(false)
const isStatusSubmitting = ref(false)
const advertisementToUpdate = ref({})
const newStatusData = ref({
  status: null,
  notifyClient: true
})

const statusOptions = [
  { title: 'Em Análise', value: 'in_analysis' },
  { title: 'Aprovado', value: 'approved' },
  { title: 'Expirado', value: 'expired' }, 
  { title: 'Rejeitado', value: 'rejected' }
]

const categoryOptions = computed(() => {
    return categories.value.map(cat => ({
        id: cat.id,
        description: cat.description
    }));
})

const statusTagMap = {
  in_analysis: 'info',
  approved: 'success',
  rejected: 'error',
  expired: 'warning'
}

const isActiveTagMap = {
  0: 'error',
  1: 'success'
}

const filteredAdvertisements = computed(() => {
  let result = advertisements.value

  if (searchTerm.value) {
    const term = searchTerm.value.toLowerCase()
    result = result.filter(
      (ad) =>
        ad.title.toLowerCase().includes(term) ||
        ad.city.toLowerCase().includes(term) ||
        ad.code.toLowerCase().includes(term) ||
        ad.address.toLowerCase().includes(term)
    )
  }

  if (selectedCategory.value) {
    result = result.filter(
      (ad) => ad.category_id === selectedCategory.value
    )
  }

  if (selectedStatus.value) {
    result = result.filter(
      (ad) => ad.status === selectedStatus.value
    )
  }

  totalItems.value = result.length
  return result
})

const paginatedAdvertisements = computed(() => {
  const start = (page.value - 1) * itemsPerPage.value
  const end = start + itemsPerPage.value
  return filteredAdvertisements.value.slice(start, end)
})

const noDataMessage = computed(() => {
  if (isLoading.value) return 'Carregando...'
  if (filteredAdvertisements.value.length === 0) return 'Nenhum anúncio encontrado.'
  return ''
})

const columns = [
  { title: 'Código', key: 'code', sortable: false },
  { title: 'Título', key: 'title', sortable: true },
  { title: 'Categoria', key: 'category', sortable: true }, 
  { title: 'Preço', key: 'amount', sortable: true }, 
  { title: 'Status', key: 'status', sortable: true },
  { title: 'Destaque', key: 'is_featured', sortable: true }, 
  { title: 'Ativo', key: 'is_active', sortable: true },
  { title: 'Ações', key: 'actions', sortable: false }
]


const loadAdvertisements = async () => {
  isLoading.value = true
  hasError.value = false
  loadingStore.show() 
  try {
    const response = await api.get('/advertisements/fetch')

    const enterpriseData = response.data.data[0] 

    //if (enterpriseData && enterpriseData.advertisements) {
      advertisements.value = response.data.data
    /*} else {
      if (Array.isArray(response.data.data)) {
         advertisements.value = response.data.data
      } else {
         advertisements.value = []
         console.warn('The API response did not contain the expected advertisement list.')
      }
    }*/
    
    page.value = 1 
  } catch (error) {
    console.error('Erro ao carregar anúncios:', error)
    hasError.value = true
    toastStore.showToast({
      message:
        error?.response?.data?.error ||
        'Erro ao carregar a lista de anúncios.',
      color: 'error'
    })
  } finally {
    isLoading.value = false
    loadingStore.hide() 
  }
}

const loadCategories = async () => {
    try {
        const params = {
            per_page: 999,
            is_active: 1
        }
        
        const response = await api.get('/categories/fetch', { params })
        
        // Usando o retorno da API fornecido: { "data": [ ... ] }
        categories.value = response.data.data
        
    } catch (error) {
        console.error('Erro ao carregar categorias:', error)
        toastStore.showToast({
            message: error?.response?.data?.error || 'Erro ao carregar a lista de categorias.',
            color: 'error'
        })
    }
    // Nota: Removido o 'finally' com isLoading.value = false para evitar conflito
    // com a flag de carregamento principal, mantida no loadAdvertisements.
}

const handleCreateAdvertisement = () => {
  router.push('/app/anuncios/novo')
}

const handleEdit = async (row) => {
  router.push({ name: 'advertisement-edit', params: { id: row.id } }) 
}

const handleDelete = (item) => {
  itemToDelete.value = item
  deleteDialog.value = true
}

const confirmDelete = async () => {
  deleteDialog.value = false
  try {
    await api.delete(`/advertisements/${itemToDelete.value.id}`)
    toastStore.showToast({
      message: 'Anúncio excluído com sucesso!',
      color: 'success'
    })
    await loadAdvertisements()
  } catch (error) {
    console.error('Erro ao excluir anúncio:', error)
    toastStore.showToast({
      message:
        error?.response?.data?.error || 'Erro ao excluir anúncio.',
      color: 'error'
    })
  }
}

const handleStatusChange = (item) => {
  advertisementToUpdate.value = item
  newStatusData.value.status = item.status
  statusDialog.value = true
}

const confirmStatusChange = async () => {
  isStatusSubmitting.value = true

  try {
    const payload = {
      status: newStatusData.value.status,
      notify_client: newStatusData.value.notifyClient
    }

    await api.put(`/advertisements/${advertisementToUpdate.value.id}/status`, payload)

    toastStore.showToast({
      message: 'Status do anúncio atualizado com sucesso!',
      color: 'success'
    })

    await loadAdvertisements()

    statusDialog.value = false
  } catch (error) {
    console.error('Erro ao alterar status:', error)
    toastStore.showToast({
      message:
        error?.response?.data?.error || 'Erro ao alterar o status do anúncio.',
      color: 'error'
    })
  } finally {
    isStatusSubmitting.value = false
  }
}

function formatStatus(status) {
  const map = {
    in_analysis: 'Em Análise',
    approved: 'Aprovado',
    rejected: 'Rejeitado',
    expired: 'Expirado' // Adicionado
  }
  return map[status] || status
}

function formatIsFeatured(is_featured) {
  return is_featured === 1 ? 'Sim' : 'Não'
}

function formatIsActive(is_active) {
  return is_active === 1 ? 'Ativo' : 'Desativado'
}

function onFiltersChange() {
  page.value = 1
}

onMounted(async () => {
  const storedData = localStorage.getItem('userData')
  if (storedData) {
    userData.value = JSON.parse(storedData)
  }
  await loadAdvertisements();
  await loadCategories();
})
</script>